'use client';

import Image from 'next/image';
import { siteService } from '@/services/site.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useCallback, useState, useEffect } from 'react';

type ImageProps = React.ComponentProps<typeof Image>;
type OverrideOrExtendProps = {
	src: string,
	isGalaxyFolder: boolean,
	fallbackSrc?: string
};
type ImageCouldFromRemoteServerProps = ImageProps & OverrideOrExtendProps;
const useActiveSrcParser = (isGalaxyFolder: boolean) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	return useCallback((src: string): string => {
		if (!SiteConfigsData?.EnableImageServer) {
			return src;
		}
		return siteService.getCombinedImagePathWithoutVersionId(src, isGalaxyFolder, SiteConfigsData.ImageServerUrl);
	}, [isGalaxyFolder, SiteConfigsData?.EnableImageServer, SiteConfigsData?.ImageServerUrl]);
};
export const ImageCouldFromRemoteServer = (props: ImageCouldFromRemoteServerProps) => {
	const activeSrcParser = useActiveSrcParser(props.isGalaxyFolder);
	const [activeSrc, setActiveSrc] = useState<string>(activeSrcParser(props.src));
	const useFallbackSrcWhenError = () => {
		if (props.fallbackSrc) {
			const updatedActiveSrc = activeSrcParser(props.fallbackSrc);
			setActiveSrc(updatedActiveSrc);
		}
	};
	const transferRawPropsIntoImageProps = (props: ImageCouldFromRemoteServerProps): ImageProps => {
		const activeImageProps: ImageCouldFromRemoteServerProps = Object.assign({}, props, { src: activeSrc, onError: useFallbackSrcWhenError });
		Object.keys(activeImageProps).forEach(key => {
			const overrideOrExtendProp = key as keyof OverrideOrExtendProps;
			if (overrideOrExtendProp in activeImageProps && ['isGalaxyFolder', 'fallbackSrc'].includes(overrideOrExtendProp)) {
				delete activeImageProps[overrideOrExtendProp as keyof ImageCouldFromRemoteServerProps];
			}
		});
		return activeImageProps;
	};

	useEffect(() => {
		setActiveSrc(activeSrcParser(props.src));
	}, [props.src, activeSrcParser]);

	return (
		<Image {...(transferRawPropsIntoImageProps(props))} />
	);
};